import axios from "axios";
import setAuthToken from "@/axios";
import Swal from "sweetalert2";
import { addFailLog } from "@/utils/failLogService";

let BASE_URL = null;
let debug_mode = false;
let onlineType = false;
// const token = 12345;
const token = localStorage.getItem("token");
setAuthToken(token);

const currentHost = window.location.host;
if (currentHost === "app.casterp.host") {
    BASE_URL = "https://production.casterp.host/api";
    debug_mode = false;
    onlineType = true;
} else if (currentHost === "komt.casterp.host") {
    BASE_URL = "https://development.casterp.host/api";
    onlineType = true;
    debug_mode = true;
} else if (currentHost === "localhost:3001") {
    BASE_URL = "http://localhost:8000/api";
    debug_mode = true;
} else if (currentHost === "testing.casterp.host") {
    BASE_URL = "https://staging.casterp.host/api";
    debug_mode = true;
} else {
    // BASE_URL = "https://development.casterp.host/api";
    BASE_URL = "https://staging.casterp.host/api";
    debug_mode = true;
}

if (debug_mode) {
    console.log(token);
    console.log("Current Server:", onlineType ? "Online" : "Local");
}

const company_id = localStorage.getItem("company_id");

// Global controller used to cancel in-flight GET requests on route change.
// Write operations (post/put/delete) are intentionally NOT tied to this,
// because aborting them client-side doesn't stop the backend from executing.
let globalGetController = new AbortController();
let pendingAbortController = null;

/** Swap to a fresh controller before navigation; abort the old one in afterEach. */
export function swapGetControllerForNavigation() {
    pendingAbortController = globalGetController;
    globalGetController = new AbortController();
}

export function abortNavigationGetController() {
    if (pendingAbortController) {
        pendingAbortController.abort();
        pendingAbortController = null;
    }
}

// 统一处理认证失败的函数
function handleAuthError(production = false) {
    if (!production) {
        console.error(
            "Authentication failed. Token may be invalid or expired.",
        );
        return;
    }
    localStorage.removeItem("token");
    localStorage.removeItem("company_id");
    Swal.fire({
        icon: "error",
        title: "Authentication Failed",
        text: "Your session has expired or token is invalid. Please login again.",
    }).then(() => {
        window.location.href = "/";
    });
}

// 统一处理错误响应
function handleErrorResponse(error, showAlert = true, logContext = {}) {
    const statusCode = error.response ? error.response.status : null;

    // 处理网络错误
    if (error.message === "Network Error") {
        addFailLog({
            ...logContext,
            statusCode: null,
            errorMessage: "Network Error",
        });
        return {
            status: false,
            message: "Network Error",
        };
    }

    // 处理取消的请求
    if (axios.isCancel(error)) {
        return {
            status: false,
            message: "Request cancelled",
        };
    }

    // 处理 401 认证错误 - 始终强制登出
    if (error.response && error.response.status === 401) {
        addFailLog({
            ...logContext,
            statusCode,
            errorMessage: "Authentication failed (401)",
        });
        handleAuthError(true);
        return {
            status: false,
            message: "Authentication failed",
        };
    }

    // 处理 403 权限错误
    if (error.response && error.response.status === 403) {
        addFailLog({
            ...logContext,
            statusCode,
            errorMessage: "Authentication failed (403)",
        });
        handleAuthError(onlineType);
        return {
            status: false,
            message: "Authentication failed",
        };
    }

    // 处理其他错误
    let extra_msg = "Something went wrong.";
    if (error.response && error.response.data && error.response.data.message) {
        extra_msg = error.response.data.message;
    }

    addFailLog({ ...logContext, statusCode, errorMessage: extra_msg });

    if (showAlert) {
        Swal.fire({
            icon: "error",
            title: "Oops...",
            text: extra_msg,
        });
    }

    return {
        status: false,
        message: extra_msg,
    };
}

async function fetchData(
    API_URL,
    type,
    params = {},
    file = false,
    signal = null,
) {
    let response;
    const url = `${BASE_URL}/${API_URL}`;

    try {
        switch (type) {
            case "put": {
                const putParams = {
                    current_company_id: company_id,
                    ...params,
                };
                response = await axios.put(url, putParams);
                break;
            }
            case "delete":
                response = await axios.delete(url);
                break;
            case "post":
                response = await axios.post(url, params);
                break;
            case "get":
            default: {
                const localParams = {
                    current_company_id: company_id,
                };
                const allParams = { ...localParams, ...params };
                if (file) {
                    response = await axios.get(url, {
                        responseType: "blob",
                        params: allParams,
                    });
                    return response;
                } else {
                    response = await axios.get(url, {
                        params: allParams,
                        // Fall back to the global controller so route changes
                        // can cancel page-level GETs without callers opting in.
                        signal: signal ?? globalGetController.signal,
                    });
                }
            }
        }
    } catch (error) {
        // Cancelled requests (route change / component abort): never resolve,
        // so callers don't run their error handlers (e.g. Swal on res.message).
        // The pending promise is garbage-collected with the leaving page.
        if (axios.isCancel(error)) {
            return new Promise(() => {});
        }
        if (file && error.response?.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const parsed = JSON.parse(text);
                if (parsed && typeof parsed === "object") {
                    error.response.data = parsed;
                }
            } catch {
                // Non-JSON blob errors keep the generic message.
            }
        }
        return handleErrorResponse(error, type !== "post", {
            apiUrl: API_URL,
            method: type,
            params,
        });
    }

    if (response.status !== 200) {
        return response;
    }
    return response.data;
}

export default fetchData;
